var __accessCheck = (obj, member, msg) => {
  if (!member.has(obj))
    throw TypeError("Cannot " + msg);
};
var __privateGet = (obj, member, getter) => {
  __accessCheck(obj, member, "read from private field");
  return getter ? getter.call(obj) : member.get(obj);
};
var __privateAdd = (obj, member, value) => {
  if (member.has(obj))
    throw TypeError("Cannot add the same private member more than once");
  member instanceof WeakSet ? member.add(obj) : member.set(obj, value);
};
var __privateSet = (obj, member, value, setter) => {
  __accessCheck(obj, member, "write to private field");
  setter ? setter.call(obj, value) : member.set(obj, value);
  return value;
};
var __privateWrapper = (obj, member, setter, getter) => {
  return {
    set _(value) {
      __privateSet(obj, member, value, setter);
    },
    get _() {
      return __privateGet(obj, member, getter);
    }
  };
};

// src/utils/logger.ts
var LogLevel;
(function(LogLevel2) {
  LogLevel2[LogLevel2["debug"] = 0] = "debug";
  LogLevel2[LogLevel2["info"] = 1] = "info";
  LogLevel2[LogLevel2["warn"] = 2] = "warn";
  LogLevel2[LogLevel2["error"] = 3] = "error";
})(LogLevel || (LogLevel = {}));
var logLevel = 1;
function setGlobalLogLevel(level) {
  logLevel = level;
}
var Logger = class {
  constructor(name, handler = defaultHandler) {
    this.name = name;
    this._handler = handler;
  }
  debug(...args) {
    if (logLevel <= 0) {
      this._handler(0, this.name, ...args);
    }
  }
  info(...args) {
    if (logLevel <= 1) {
      this._handler(1, this.name, ...args);
    }
  }
  warn(...args) {
    if (logLevel <= 2) {
      this._handler(2, this.name, ...args);
    }
  }
  error(...args) {
    if (logLevel <= 3) {
      this._handler(3, this.name, ...args);
    }
  }
};
function defaultHandler(level, name, ...args) {
  switch (level) {
    case 0:
      console.debug(`[DEBUG] ${name}`, ...args);
      break;
    case 1:
      console.info(`[INFO] ${name}`, ...args);
      break;
    case 2:
      console.warn(`[WARN] ${name}`, ...args);
      break;
    case 3:
      console.error(`[ERROR] ${name}`, ...args);
      break;
  }
}

// src/utils/fetch.ts
var HTTPError = class extends Error {
  constructor(message, status) {
    super(message);
    this.name = "HTTPError";
    this.status = status;
  }
};
var BackoffRetryError = class extends HTTPError {
  constructor(message, millis) {
    super(message, 429);
    this.name = "BackoffRetry";
    this.millis = millis;
  }
};
var FETCH_TIMEOUT = 3e3 * 2;
var OFFGRID_DURATION_ZERO = 0;
var OFFGRID_DEFAULT_DURATION = 30 * 1e3;
var OFFGRID_RESPONSE_HEADER = "offgrid-duration";
var BACKOFF_RESPONSE_HEADER = "backoff-retry-after-millis";
var LOGIN_SIGNAL_HEADER = "login-signal";
var CSRF_STATUS_HEADER = "csrf-status";
var CSRF_REJECTED = "rejected";
var CSRF_RESPONSE_HEADER = "nrk-proxy-csrf";
var CSRF_REQUEST_HEADER = "nrk-proxy-csrf";
var SESSION_STORAGE_CSRF_LAST_INIT_KEY = "nrk-login-csrf-load-time";
var LOCAL_STORAGE_CSRF_LAST_INIT_KEY = "nrk-login-csrf-load-time";
var csrfToken = "";
function get(href, signal) {
  return doFetchWithBackoffRetry(href, {
    method: "GET",
    signal
  });
}
function post(href, body, session, signal) {
  return doFetchWithBackoffRetry(href, {
    method: "POST",
    signal,
    body: body ? JSON.stringify(body) : void 0,
    session
  });
}
function put(href, body, session, signal) {
  return doFetchWithBackoffRetry(href, {
    method: "PUT",
    signal,
    body: JSON.stringify(body),
    session
  });
}
function shouldInitCsrf(method) {
  if (method === "GET") {
    return false;
  }
  if (csrfToken === void 0 || csrfToken === "") {
    return true;
  }
  const sessionValue = sessionStorage.getItem(SESSION_STORAGE_CSRF_LAST_INIT_KEY);
  if (sessionValue === void 0 || sessionValue === null) {
    return true;
  }
  const localValue = localStorage.getItem(LOCAL_STORAGE_CSRF_LAST_INIT_KEY);
  if (localValue === void 0 || localValue === null) {
    return true;
  }
  if (localValue > sessionValue) {
    return true;
  }
  return false;
}
async function doCsrfInit(signal) {
  const abortController = new AbortController();
  if (signal) {
    signal.addEventListener("abort", abortController.abort);
  }
  let response = void 0;
  let timeoutId = 0;
  try {
    timeoutId = window.setTimeout(() => {
      abortController.abort();
    }, FETCH_TIMEOUT);
    const headers = new Headers({ accept: "application/json", "nrk-fetch": "1" });
    const method = "POST";
    const options = {
      credentials: "include",
      headers,
      method,
      signal: abortController.signal
    };
    var href = "/auth/csrf_init";
    response = await fetch(href, options);
    if (response.headers.has(CSRF_RESPONSE_HEADER)) {
      const csrf = response.headers.get(CSRF_RESPONSE_HEADER) || "<not set>";
      updateKnownCsrfToken(csrf);
    }
  } catch (error) {
    console.error("could not initialize csrf token properly", error);
  } finally {
    window.clearTimeout(timeoutId);
    if (signal) {
      signal.removeEventListener("abort", abortController.abort);
    }
  }
}
function updateKnownCsrfToken(csrf) {
  csrfToken = csrf;
  try {
    const prefix = csrf.substring(0, csrf.indexOf("."));
    sessionStorage.setItem(SESSION_STORAGE_CSRF_LAST_INIT_KEY, prefix);
    localStorage.setItem(LOCAL_STORAGE_CSRF_LAST_INIT_KEY, prefix);
  } catch (error) {
    console.error("could not set csrf token info in local storage and session storage", error);
  }
}
async function doFetchWithBackoffRetry(href, props) {
  const result = await doFetch(href, props);
  if (result.error && result.error instanceof BackoffRetryError) {
    const millisToWait = result.error.millis;
    await delay(millisToWait);
    return await doFetch(href, props);
  }
  return result;
}
async function doFetch(href, props) {
  var _a, _b, _c;
  props.method = (_a = props.method) != null ? _a : "POST";
  const abortController = new AbortController();
  if (props == null ? void 0 : props.signal) {
    props.signal.addEventListener("abort", abortController.abort);
  }
  let response = void 0;
  let offgridTimeout = OFFGRID_DURATION_ZERO;
  let hasTimedOut = false;
  let timeoutId = 0;
  try {
    timeoutId = window.setTimeout(() => {
      hasTimedOut = true;
      abortController.abort();
    }, FETCH_TIMEOUT);
    const headers = new Headers({ accept: "application/json", "nrk-fetch": "1" });
    const options = {
      credentials: "include",
      method: props.method,
      signal: abortController.signal
    };
    if (props.body) {
      options.body = props.body;
      headers.set("content-type", "application/json");
    }
    if (shouldInitCsrf(props.method)) {
      await doCsrfInit((_b = props.signal) != null ? _b : null);
    }
    if (props.method === "POST" || props.method === "PUT" || props.method == "DELETE") {
      headers.set(CSRF_REQUEST_HEADER, csrfToken);
      if ((_c = props.session) == null ? void 0 : _c.accessToken) {
        headers.set("Authorization", "Bearer " + props.session.accessToken);
      }
    }
    options.headers = headers;
    response = await fetch(href, options);
    if (response.headers.has(CSRF_RESPONSE_HEADER)) {
      const csrf = response.headers.get(CSRF_RESPONSE_HEADER) || "<not set>";
      updateKnownCsrfToken(csrf);
      headers.set(CSRF_REQUEST_HEADER, csrf);
    }
    if (response.headers.has(CSRF_STATUS_HEADER)) {
      const csrfStatus = response.headers.get(CSRF_STATUS_HEADER);
      if (csrfStatus === CSRF_REJECTED) {
        options.headers = headers;
        response = await fetch(href, options);
      }
    }
    if (response.headers.has(OFFGRID_RESPONSE_HEADER)) {
      const value = response.headers.get(OFFGRID_RESPONSE_HEADER) || OFFGRID_DURATION_ZERO.toString();
      offgridTimeout = parseInt(value) || OFFGRID_DURATION_ZERO;
    }
    if (response.status >= 500 && response.status !== 504) {
      offgridTimeout = OFFGRID_DEFAULT_DURATION;
      throw new HTTPError("authority offgrid", response.status);
    }
    if (response.headers.has(BACKOFF_RESPONSE_HEADER)) {
      const value = response.headers.get(BACKOFF_RESPONSE_HEADER) || BACKOFF_RESPONSE_HEADER.toString();
      const retryAfterMillis = parseInt(value) || 3e3;
      throw new BackoffRetryError("Too chatty, will retry after " + retryAfterMillis + " millis", retryAfterMillis);
    }
    if (!response.ok) {
      try {
        console.error(await response.json());
      } catch (e) {
      }
      throw new HTTPError(response.statusText || "unknown response error", response.status);
    }
  } catch (error) {
    const errorResponseBody = {
      offgridTimeout,
      status: -1
    };
    if (hasTimedOut) {
      errorResponseBody.error = new Error("response timed out");
    } else if (error instanceof BackoffRetryError) {
      errorResponseBody.error = error;
    } else if (error instanceof HTTPError) {
      errorResponseBody.error = error;
      errorResponseBody.status = error.status;
    } else if (error instanceof Error) {
      errorResponseBody.error = error;
    } else {
      errorResponseBody.error = new Error("unknown error");
    }
    return errorResponseBody;
  } finally {
    window.clearTimeout(timeoutId);
    if (props.signal) {
      props.signal.removeEventListener("abort", abortController.abort);
    }
  }
  let json;
  let loginSignal = "";
  try {
    if (response.headers.has(LOGIN_SIGNAL_HEADER)) {
      loginSignal = response.headers.get(LOGIN_SIGNAL_HEADER) || "";
    }
    if (response.status === 204) {
      json = {};
    } else {
      json = await response.json();
    }
  } catch (error) {
    const errorResponseBody = {
      offgridTimeout,
      status: -1
    };
    errorResponseBody.error = error instanceof Error ? error : new Error("unknown error");
    return errorResponseBody;
  }
  return {
    body: json,
    offgridTimeout,
    status: response.status,
    loginSignal
  };
}
function delay(millis) {
  return new Promise((resolve) => setTimeout(resolve, millis));
}

// src/utils/storage.ts
function getStoredState(key) {
  const data = localStorage.getItem(key);
  if (data) {
    return JSON.parse(data);
  }
}
function setStoredState(key, value) {
  if (value) {
    localStorage.setItem(key, JSON.stringify(value));
  } else {
    localStorage.removeItem(key);
  }
}
function getSessionStoredState(key) {
  const data = sessionStorage.getItem(key);
  if (data) {
    return JSON.parse(data);
  }
}
function setSessionStoredState(key, value) {
  if (value) {
    sessionStorage.setItem(key, JSON.stringify(value));
  } else {
    sessionStorage.removeItem(key);
  }
}
function getCookie(name) {
  const key = `${name}=`;
  const cookie = document.cookie.split(";").map((item) => item.trim()).find((item) => item.startsWith(key));
  return cookie ? cookie.split("=")[1] : void 0;
}
function setCookie(name, value, expiryInMillis) {
  const expires = new Date(Date.now() + expiryInMillis);
  document.cookie = `${name}=${value};expires=$${expires.toUTCString()};path=/`;
}
function clearCookie(name) {
  const expires = new Date(0);
  document.cookie = `${name}=;expires=${expires.toUTCString()};path=/`;
}

// src/login-client/constants.ts
var ENDPOINT_GENERATE_INVITE_CODE = "/auth/invitecode/generate";
var ENDPOINT_PROFILE_DATA = "/auth/updateprofiledata";
var ENDPOINT_TOKEN_FOR_SUB = "/auth/session/tokenforsub/{sub}";
var ENDPOINT_CONTEXT_INFO = "/auth/contextinfo";
var ENDPOINT_GENERATE_URL_WITH_EXPIRY = "/auth/urlwithexpiry/generate";
var SERVER_SIGNAL_COOKIE_NAME = "login_signal";
var SERVER_LOGGED_IN_FLAG_COOKIE_NAME = "nrk-auth-flag";
var CURRENT_STATE_STORAGE_KEY = "nrk-login-client-state";
var STATE_ARCHIVE_STORAGE_KEY = "nrk-login-client-archive";
var CURRENT_IDENTITY_SESSION_STORAGE_KEY = "nrk-login-client-identity";
var CONTEXT_INFO_COOKIE_KEY = "nrk-login-context-info";
var ServerLoginSignal;
(function(ServerLoginSignal2) {
  ServerLoginSignal2["refresh"] = "refresh";
  ServerLoginSignal2["refreshWithDefaultIdentity"] = "refresh_default_identity";
  ServerLoginSignal2["refreshOpsession"] = "refresh_opsession";
  ServerLoginSignal2["logout"] = "signout";
  ServerLoginSignal2["logoutOpsession"] = "signout_opsession";
  ServerLoginSignal2["reauthenticate"] = "reauthenticate";
})(ServerLoginSignal || (ServerLoginSignal = {}));

// src/debug/debug.ts
var AUTHORITY_OVERRIDE_COOKIE_KEY = "login_authority_override";
var DEBUG_COOKIE_KEY = "nrkdebug";
var LOGIN_DISABLED_COOKIE_KEY = "nrk-disable-login-in-client";
var _client, _core, _loginDisabled;
var Debug = class {
  constructor(client, core) {
    __privateAdd(this, _client, void 0);
    __privateAdd(this, _core, void 0);
    __privateAdd(this, _loginDisabled, void 0);
    __privateSet(this, _client, client);
    __privateSet(this, _loginDisabled, getCookie(LOGIN_DISABLED_COOKIE_KEY) === "1");
    __privateSet(this, _core, core);
  }
  get _client() {
    return __privateGet(this, _client);
  }
  get loginDisabled() {
    return __privateGet(this, _loginDisabled);
  }
  set loginDisabled(disabled) {
    try {
      __privateSet(this, _loginDisabled, disabled);
      if (disabled) {
        setCookie(LOGIN_DISABLED_COOKIE_KEY, "1", 60 * 60 * 1e3);
      } else {
        clearCookie(LOGIN_DISABLED_COOKIE_KEY);
      }
      this.doRefresh(true);
    } catch (err) {
      this._handleError(err);
    }
  }
  get authorityOverride() {
    try {
      return getCookie(AUTHORITY_OVERRIDE_COOKIE_KEY);
    } catch (err) {
      return void 0;
    }
  }
  async getCurrentAuthorityUrl() {
    var _a, _b;
    const response = await post("/auth/debug/currentAuthority");
    if (response.status == 200) {
      return (_b = (_a = response.body) == null ? void 0 : _a.frontchannelUri) != null ? _b : "https://innlogging.nrk.no";
    }
    if (this.authorityOverride) {
      return this.authorityOverride;
    }
    return "https://innlogging.nrk.no";
  }
  async prepInviteCodeNeverDoThisInProduction() {
    const response = await post("/auth/debug/generateInviteCodeChallenge", {
      iSolemnlySwear: "ThisIsNotProduction"
    }, await __privateGet(this, _client).getSession());
    if (response.status == 200 && response.body) {
      return response.body;
    }
    throw "Could not prep invite code";
  }
  async redeemInviteCodeAndReturnSessionNeverDoThisInProduction(code, pkce) {
    const response = await post("/auth/debug/redeemInviteCode", {
      inviteCode: code,
      codeVerifier: pkce.codeVerifier
    });
    if (response.status == 200 && response.body) {
      return response.body;
    }
    throw "Could not redeem invite code";
  }
  async overrideAuthority(newAuthority) {
    const oldAuthority = this.authorityOverride;
    if (oldAuthority !== newAuthority) {
      try {
        if (newAuthority) {
          setCookie(AUTHORITY_OVERRIDE_COOKIE_KEY, newAuthority, 1e3 * 60 * 60 * 24 * 365);
        } else {
          clearCookie(AUTHORITY_OVERRIDE_COOKIE_KEY);
        }
        __privateGet(this, _core)._updateState({
          state: "LoggedOut",
          userAction: "Nothing"
        });
        clearCookie(CONTEXT_INFO_COOKIE_KEY);
        await __privateGet(this, _client)._updateContextInfo();
      } catch (err) {
        this._handleError(err);
      }
    }
  }
  setEnvironment(environment) {
    try {
      switch (environment) {
        case "test":
          this.overrideAuthority("https://test-innlogging.nrk.no");
          return;
        case "stage":
          this.overrideAuthority("https://stage-innlogging.nrk.no");
          return;
        case "preprod":
          this.overrideAuthority("https://preprod-innlogging.nrk.no");
          return;
        case "prod":
          this.overrideAuthority("https://innlogging.nrk.no");
          return;
        case "custom":
          throw new Error("cannot set environment to 'custom', use overrideAuthority() instead");
        default:
          this.overrideAuthority(void 0);
      }
    } catch (err) {
      this._handleError(err);
    }
  }
  get environment() {
    const auth = this.authorityOverride;
    switch (auth) {
      case void 0:
        return void 0;
      case "https://test-innlogging.nrk.no":
        return "test";
      case "https://stage-innlogging.nrk.no":
        return "stage";
      case "https://preprod-innlogging.nrk.no":
        return "preprod";
      case "https://innlogging.nrk.no":
        return "prod";
      default:
        return "custom";
    }
  }
  get networkDebugging() {
    try {
      const setting = getCookie(DEBUG_COOKIE_KEY);
      if (setting === "on") {
        return "_no_case_id_";
      }
      if (setting && setting.startsWith("on-")) {
        return setting.substr(3);
      }
      return void 0;
    } catch (err) {
      this._handleError(err);
      return void 0;
    }
  }
  set networkDebugging(caseId) {
    try {
      if (caseId) {
        setCookie(DEBUG_COOKIE_KEY, "on-" + caseId, 5 * 60 * 1e3);
      } else if (caseId === "") {
        setCookie(DEBUG_COOKIE_KEY, "on", 5 * 60 * 1e3);
      } else {
        clearCookie(DEBUG_COOKIE_KEY);
      }
    } catch (err) {
      this._handleError(err);
    }
  }
  setOffgrid(millis) {
    __privateGet(this, _core).goOffgrid(millis);
  }
  async clearOffgrid() {
    this.doRefresh(true);
  }
  async doRefresh(_force) {
    await __privateGet(this, _client)._refresh();
  }
  async refresh() {
    return await __privateGet(this, _client)._refresh();
  }
  _handleError(err) {
    __privateGet(this, _client)._handleError(err);
  }
  getAppQrCodeUrl(url) {
    return url.replace("app-qr", "app-qr2");
  }
  async doGet(url) {
    return await get(url);
  }
  async doPost(url, data, sessionData) {
    return await post(url, data, sessionData);
  }
  async doPut(url, data, sessionData) {
    return await put(url, data, sessionData);
  }
  async logInTestUsers(options) {
    const url = new URL("/auth/testusers/login", window.location.href);
    const response = await post(url.href, options, void 0);
    await __privateGet(this, _core)._handleSessionDataFetchResponse(response, void 0, void 0);
  }
  async setRequirementsForTestUser(sub, requirements) {
    const session = await __privateGet(this, _core)._changeIdentity(sub);
    const url = new URL("/auth/testusers/setrequirements", window.location.href);
    const response = await post(url.href, requirements, session);
    await __privateGet(this, _core)._handleSessionDataFetchResponse(response, void 0, void 0);
  }
};
_client = new WeakMap();
_core = new WeakMap();
_loginDisabled = new WeakMap();

// src/utils/safe-parseint.ts
function safeParseInt(input, defaultValue) {
  const parsed = parseInt(input, 10);
  return Number.isInteger(parsed) ? parsed : defaultValue;
}

// src/login-client/login-session.ts
var _state;
var LoginSession = class {
  constructor(state) {
    __privateAdd(this, _state, void 0);
    __privateSet(this, _state, state);
  }
  get _state() {
    return __privateGet(this, _state);
  }
  get accessToken() {
    if (__privateGet(this, _state).sessionData === void 0 || Date.now() > __privateGet(this, _state).sessionData.hardExpiry - 5) {
      return void 0;
    }
    return __privateGet(this, _state).sessionData.accessToken;
  }
  get expires() {
    return __privateGet(this, _state).expires;
  }
  get serverEpochExpiry() {
    var _a;
    return (_a = __privateGet(this, _state).sessionData) == null ? void 0 : _a.serverEpochExpiry;
  }
  get idToken() {
    if (__privateGet(this, _state).sessionData === void 0 || Date.now() > __privateGet(this, _state).sessionData.softExpiry) {
      return void 0;
    }
    return __privateGet(this, _state).sessionData.idToken;
  }
  get isExpired() {
    if (!this.isLoggedIn && !this.isOffgrid) {
      return false;
    }
    return Date.now() > __privateGet(this, _state).expires;
  }
  get isLoggedIn() {
    return __privateGet(this, _state).sessionData !== void 0;
  }
  get isOffgrid() {
    const offgridState = __privateGet(this, _state).isOffgrid;
    const expires = __privateGet(this, _state).expires;
    const ret = offgridState && Date.now() < expires;
    return ret;
  }
  get user() {
    var _a;
    return (_a = __privateGet(this, _state).sessionData) == null ? void 0 : _a.user;
  }
  get identities() {
    var _a;
    return (_a = __privateGet(this, _state).sessionData) == null ? void 0 : _a.identities;
  }
  get userProblem() {
    var _a;
    return (_a = __privateGet(this, _state).sessionData) == null ? void 0 : _a.userProblem;
  }
  toJSON() {
    return __privateGet(this, _state);
  }
  userConformsToRequirements(input) {
    if (!this.user) {
      return false;
    }
    if (input.requirements === void 0) {
      return true;
    }
    for (const r of input.requirements) {
      let requirement = r;
      let version = 1;
      if (r.indexOf(":") !== -1) {
        const parts = r.split(":");
        requirement = parts[0];
        version = safeParseInt(parts[1], 1);
      }
      if (requirement === "portability" && this.user.claims["nrk/cor"] === "no") {
        continue;
      }
      if (requirement === "portability" && this.user.claims["nrk/cor"] !== "no") {
        return false;
      }
      if (this.user.claims["nrk/consent/" + requirement] === void 0) {
        return false;
      }
      if (safeParseInt(this.user.claims["nrk/consent/" + requirement], 0) < version) {
        return false;
      }
    }
    return true;
  }
};
_state = new WeakMap();

// src/utils/visibilitychange.ts
function actionOnvisible(action) {
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible" && action) {
      action();
    }
  });
}

// src/utils/delay.ts
function delay2(millis) {
  return new Promise((resolve) => setTimeout(resolve, millis));
}

// src/login-client/client-core.ts
var DEFAULT_ANONYMOUS_SESSION_STATE = { expires: 0, isOffgrid: false, sessionData: void 0 };
var DEFAULT_OFFGRID_MILLIS = 1e3 * 60 * 5;
var _session, _fetchSessionPromise, _refreshTimeoutId, _nextRefresh, _lastForceRefresh;
var ClientCore = class {
  constructor(config, logger, internalComms) {
    __privateAdd(this, _session, void 0);
    this._loginDisabled = false;
    __privateAdd(this, _fetchSessionPromise, void 0);
    __privateAdd(this, _refreshTimeoutId, void 0);
    __privateAdd(this, _nextRefresh, void 0);
    __privateAdd(this, _lastForceRefresh, Date.now());
    this.config = config;
    this.logger = logger;
    this.sessionState = {
      isOffgrid: DEFAULT_ANONYMOUS_SESSION_STATE.isOffgrid,
      expires: DEFAULT_ANONYMOUS_SESSION_STATE.expires,
      sessionData: DEFAULT_ANONYMOUS_SESSION_STATE.sessionData
    };
    __privateSet(this, _session, new LoginSession(this.sessionState));
    this.internalComms = internalComms;
    actionOnvisible(async () => await this.checkOfGridAndToken());
  }
  bootstrap() {
    const serverSignal = getCookie(SERVER_SIGNAL_COOKIE_NAME);
    this.logger.debug(`server signal: ${serverSignal}`);
    if (serverSignal) {
      switch (serverSignal) {
        case ServerLoginSignal.refresh:
          this.logger.debug("starting to refresh session (server told me so)");
          this._refresh().then(() => {
            clearCookie(SERVER_SIGNAL_COOKIE_NAME);
          });
          return;
        case ServerLoginSignal.refreshWithDefaultIdentity:
          this.logger.debug("starting to refresh session with default identity (server told me so)");
          this._refresh("_").then(() => {
            clearCookie(SERVER_SIGNAL_COOKIE_NAME);
          });
          return;
        case ServerLoginSignal.refreshOpsession:
          this._refresh().then(() => {
            this.logger.info("opsession logged in");
            this.dispatchUpdateEvent("opsession_login", { detail: this._session });
            clearCookie(SERVER_SIGNAL_COOKIE_NAME);
          });
          return;
        case ServerLoginSignal.reauthenticate:
          clearCookie(SERVER_SIGNAL_COOKIE_NAME);
          this.internalComms.reauthenticate();
          return;
        case ServerLoginSignal.logout:
          clearCookie(SERVER_SIGNAL_COOKIE_NAME);
          this._updateState({ state: "LoggedOut", userAction: "Nothing" });
          return;
        case ServerLoginSignal.logoutOpsession:
          clearCookie(SERVER_SIGNAL_COOKIE_NAME);
          this._updateState({ state: "LoggedOut", userAction: "Nothing" });
          this.logger.info("opsession logged out");
          window.setTimeout(() => {
            this.dispatchUpdateEvent("opsession_logout", { detail: this._session });
          }, 10);
          return;
      }
    } else {
      this._syncLoginStatusWithServer();
    }
    const sessionIdentity = this._loadSessionIdentity();
    const checkedSessionIdentity = this._filterOutUnavailableIdentity(sessionIdentity);
    const storedState = this._loadSessionState(checkedSessionIdentity);
    if (storedState) {
      this.sessionState.expires = storedState.expires;
      this.sessionState.isOffgrid = storedState.isOffgrid;
      this.sessionState.sessionData = storedState.sessionData;
      this.logger.debug("bootstrapped state:", storedState);
      if (storedState.isOffgrid || storedState.sessionData !== void 0) {
        this._scheduleRefresh(storedState.expires, true);
      }
    }
  }
  async _syncLoginStatusWithServer() {
    const flagSaysLoggedIn = getCookie(SERVER_LOGGED_IN_FLAG_COOKIE_NAME) ? true : false;
    const session = await this.getSession();
    if (flagSaysLoggedIn && !session.isLoggedIn) {
      this.internalComms.reauthenticate();
    } else if (!flagSaysLoggedIn && session.isLoggedIn) {
      this._updateState({ state: "LoggedOut", userAction: "Nothing" });
    }
  }
  get _session() {
    return __privateGet(this, _session);
  }
  set _session(session) {
    __privateSet(this, _session, session);
  }
  get _fetchSessionPromise() {
    return __privateGet(this, _fetchSessionPromise);
  }
  get _refreshTimeoutId() {
    return __privateGet(this, _refreshTimeoutId);
  }
  get _nextRefresh() {
    return __privateGet(this, _nextRefresh);
  }
  get _lastForceRefresh() {
    return __privateGet(this, _lastForceRefresh);
  }
  async getSession() {
    this.logger.debug("starting to get session");
    const ongoingFetch = __privateGet(this, _fetchSessionPromise);
    if (ongoingFetch) {
      this.logger.debug("ongoing fetch for session found, awaiting that result");
      return await ongoingFetch;
    }
    if (!this._session.isExpired) {
      this.logger.debug("found data fresh enough in local variable, using that");
      if (this._session.isOffgrid) {
        var remainingOffgrid = this._session.expires - new Date().getTime();
        this.logger.debug("unfortunately, we are offgrid at the moment, will try to resume ongrid in " + remainingOffgrid + " seconds");
      }
      return this._session;
    }
    return await this._refresh();
  }
  _scheduleRefresh(timestamp, force = false) {
    window.clearTimeout(__privateGet(this, _refreshTimeoutId));
    __privateSet(this, _nextRefresh, timestamp);
    __privateSet(this, _refreshTimeoutId, void 0);
    const duration = timestamp - Date.now();
    if (!force && duration < 0) {
      this.logger.warn(`attempted to schedule refresh with negative duration: ${duration}`);
      return;
    }
    const shouldRevertOffgridWhenLoggedOut = this._session.isOffgrid && !this._session.isLoggedIn;
    const refreshTimeoutComplete = () => {
      if (shouldRevertOffgridWhenLoggedOut) {
        this._updateState({ state: "LoggedOut", userAction: "Nothing" });
      } else {
        this._refresh();
      }
    };
    if (duration <= 0) {
      refreshTimeoutComplete();
    } else {
      const refreshWillHappenAt = Date.now() + duration;
      this.logger.debug(`scheduled ${force ? "forced " : ""}refresh for: ${new Date(refreshWillHappenAt).toTimeString()}`);
      __privateSet(this, _refreshTimeoutId, window.setTimeout(refreshTimeoutComplete, duration));
    }
  }
  _refresh(wantedSub) {
    if (!__privateGet(this, _fetchSessionPromise)) {
      this.logger.debug("no ongoing fetch, starting to refresh");
      __privateSet(this, _fetchSessionPromise, this._fetchSessionState(wantedSub).then((sessionFetchResponse) => {
        __privateSet(this, _fetchSessionPromise, void 0);
        console.log("---sessionFetchResponse---", sessionFetchResponse);
        return sessionFetchResponse;
      }));
    }
    this.logger.debug("ongoing fetch found, returning that result to caller");
    return __privateGet(this, _fetchSessionPromise);
  }
  async _fetchSessionState(wantedSub) {
    var _a, _b, _c, _d;
    const sub = (_c = (_b = wantedSub != null ? wantedSub : (_a = this.sessionState.sessionData) == null ? void 0 : _a.user.sub) != null ? _b : this._loadSessionIdentity()) != null ? _c : "_";
    const url = ENDPOINT_TOKEN_FOR_SUB.replace("{sub}", sub);
    const response = await post(url, void 0, this._session);
    await this._handleSessionDataFetchResponse(response, (_d = this._session.user) == null ? void 0 : _d.sub);
    return this._session;
  }
  _clearStoredSessionState() {
    setStoredState(CURRENT_STATE_STORAGE_KEY, void 0);
    setStoredState(STATE_ARCHIVE_STORAGE_KEY, void 0);
    setSessionStoredState(CURRENT_IDENTITY_SESSION_STORAGE_KEY, void 0);
  }
  _loadSessionIdentity() {
    if (this.config.enableTabCoordinationViaSessionStorage) {
      return getSessionStoredState(CURRENT_IDENTITY_SESSION_STORAGE_KEY);
    }
    return void 0;
  }
  _persistCurrentSessionState(sessionState) {
    var _a;
    setStoredState(CURRENT_STATE_STORAGE_KEY, sessionState);
    if (this.config.enableTabCoordinationViaSessionStorage) {
      setSessionStoredState(CURRENT_IDENTITY_SESSION_STORAGE_KEY, (_a = sessionState.sessionData) == null ? void 0 : _a.user.sub);
    }
  }
  _archiveKnownSessionState(sessionState) {
    var _a, _b;
    if (sessionState.sessionData === void 0) {
      setStoredState(STATE_ARCHIVE_STORAGE_KEY, { records: [] });
    } else if ((_a = sessionState.sessionData) == null ? void 0 : _a.user.sub) {
      let archive = getStoredState(STATE_ARCHIVE_STORAGE_KEY);
      if (archive === void 0) {
        archive = {
          records: []
        };
      }
      archive.records = archive.records.filter((record) => {
        var _a2;
        return record.sub !== ((_a2 = sessionState.sessionData) == null ? void 0 : _a2.user.sub);
      });
      archive.records.push({
        sub: (_b = sessionState.sessionData) == null ? void 0 : _b.user.sub,
        sessionState
      });
      archive.records.forEach((record) => {
        var _a2;
        if (record.sessionState.sessionData !== void 0 && ((_a2 = sessionState.sessionData) == null ? void 0 : _a2.identities) !== void 0) {
          record.sessionState.sessionData.identities = sessionState.sessionData.identities;
        }
      });
      setStoredState(STATE_ARCHIVE_STORAGE_KEY, archive);
    }
  }
  async _changeIdentity(sub) {
    var _a;
    this.logger.debug(`fetch session data (for new sub: ${sub})`);
    let handled = false;
    try {
      if (!this._session.isExpired && ((_a = this._session.user) == null ? void 0 : _a.sub) === sub) {
        this.logger.debug("Short-circuiting swap sub, nothing to do");
        return this._session;
      }
      const oldSession = this._loadSessionState(sub);
      if ((oldSession == null ? void 0 : oldSession.sessionData) && oldSession.expires > Date.now()) {
        await this._handleSessionDataFetchResponse({
          body: {
            session: oldSession.sessionData,
            state: "LoggedIn",
            userAction: "Nothing"
          },
          status: 200
        }, sub);
        return this._session;
      }
      const url = ENDPOINT_TOKEN_FOR_SUB.replace("{sub}", sub);
      const response = await post(url, void 0, this._session);
      handled = true;
      await this._handleSessionDataFetchResponse(response, sub, true);
    } catch (error) {
      this.logger.error("Got error while changing identity: ", error);
      if (!handled) {
        this.dispatchErrorEvent({ name: "identitychange_error", message: error });
      }
      throw error;
    }
    return this._session;
  }
  _loadSessionState(sub) {
    var _a;
    const current = getStoredState(CURRENT_STATE_STORAGE_KEY);
    if (sub === void 0) {
      return current;
    }
    if (((_a = current == null ? void 0 : current.sessionData) == null ? void 0 : _a.user.sub) === sub) {
      return current;
    }
    let archive = getStoredState(STATE_ARCHIVE_STORAGE_KEY);
    if (archive === void 0) {
      archive = {
        records: []
      };
    }
    const relevantRecords = archive.records.filter((record) => record.sub === sub);
    if (relevantRecords.length > 0) {
      return relevantRecords[0].sessionState;
    }
    return void 0;
  }
  async _handleSessionDataFetchResponse(response, expectedSub, throwOnError) {
    var _a, _b, _c, _d;
    const loginInProgress = ((_b = (_a = this._session) == null ? void 0 : _a.user) == null ? void 0 : _b.sub) === void 0;
    const identityChangeInProgress = !loginInProgress && expectedSub !== void 0 && ((_c = this._session.user) == null ? void 0 : _c.sub) !== void 0 && expectedSub !== ((_d = this._session.user) == null ? void 0 : _d.sub);
    const { error, body, offgridTimeout } = response;
    if (this._loginDisabled) {
      this.logger.warn("Login has been disabled by an innlogging-web debugging feature. Will pretend to be logged out no matter what.");
      return await this._updateState({
        state: "LoggedOut",
        userAction: "Nothing"
      }, void 0);
    }
    if (error) {
      let fallbackSessionData = this.sessionState.sessionData;
      if (expectedSub && expectedSub !== (fallbackSessionData == null ? void 0 : fallbackSessionData.user.sub)) {
        const sessionState = this._loadSessionState(expectedSub);
        fallbackSessionData = (sessionState == null ? void 0 : sessionState.sessionData) || this.sessionState.sessionData;
      }
      if (identityChangeInProgress && fallbackSessionData && (fallbackSessionData == null ? void 0 : fallbackSessionData.user.sub) && fallbackSessionData.user.sub !== expectedSub) {
        this.dispatchErrorEvent({ name: "identitychange_error", message: error.message });
        if (throwOnError) {
          throw error;
        }
      }
      if ((fallbackSessionData == null ? void 0 : fallbackSessionData.hardExpiry) && fallbackSessionData.hardExpiry > Date.now()) {
        return await this._updateState({ session: fallbackSessionData, state: "LoggedIn", userAction: "Nothing" }, 0, expectedSub);
      }
      if (fallbackSessionData) {
        return await this._updateState({ session: fallbackSessionData, state: "LoggedIn", userAction: "Nothing" }, offgridTimeout || DEFAULT_OFFGRID_MILLIS, expectedSub);
      }
      await this._updateState({
        state: "LoggedOut",
        userAction: "Nothing"
      }, offgridTimeout || DEFAULT_OFFGRID_MILLIS);
      return await this._handleError(error, throwOnError);
    }
    if (body) {
      return await this._updateState(body, offgridTimeout, expectedSub, throwOnError);
    }
  }
  async _handleUnexpectedIdentityFromServer(sessionState, identityChangeInProgress, throwOnError) {
    if (this.sessionState.sessionData !== void 0 && sessionState.sessionData !== void 0) {
      this.sessionState.sessionData.identities = sessionState.sessionData.identities;
      if (identityChangeInProgress) {
        this.dispatchCustomEvent("changeidentity_error", { detail: this._session });
        if (throwOnError) {
          throw new Error("could not change identity");
        }
      } else {
        this.dispatchCustomEvent("unexpected_identity", { detail: this._session });
        if (throwOnError) {
          throw new Error("unexpected identity");
        }
      }
    } else {
      this.logger.error("Got unexpected identity problem, but there is no current session.. weird!");
      return this._handleLogout(false);
    }
  }
  async _handleLogout(planned) {
    this._clearStoredSessionState();
    this.sessionState.isOffgrid = false;
    this.sessionState.expires = 0;
    this.sessionState.sessionData = void 0;
    const eventName = planned ? "logout_success" : "unexpected_logout";
    this.dispatchUpdateEvent(eventName, { detail: this._session });
    this.internalComms.logout();
  }
  async _handleOffgridLogout(planned, offgridTimeout) {
    this._clearStoredSessionState();
    this.sessionState.isOffgrid = true;
    this.sessionState.expires = Date.now() + offgridTimeout;
    this.sessionState.sessionData = void 0;
    const eventName = planned ? "logout_success" : "unexpected_logout";
    this.dispatchUpdateEvent(eventName, { detail: this._session });
  }
  async _updateState(responseBody, offgridTimeout = 0, expectedSub, throwOnError = false) {
    var _a, _b, _c;
    const loginInProgress = ((_a = this._session.user) == null ? void 0 : _a.sub) === void 0;
    const identityChangeInProgress = !loginInProgress && expectedSub !== void 0 && ((_b = this._session.user) == null ? void 0 : _b.sub) !== void 0 && expectedSub !== ((_c = this._session.user) == null ? void 0 : _c.sub);
    const wasOffgrid = this._session.isOffgrid;
    if (responseBody) {
      const { state, userAction } = responseBody;
      if (state === "LoggedOut" || userAction === "MustLogOut") {
        if (offgridTimeout > 0) {
          await this._handleOffgridLogout(false, offgridTimeout);
          if (!wasOffgrid) {
            this.dispatchUpdateEvent("going_offgrid", { detail: this._session });
          }
          return;
        }
        await this._handleLogout(true);
        if (wasOffgrid) {
          this.dispatchUpdateEvent("going_ongrid", { detail: this._session });
        }
        return;
      }
      if (userAction === "MustReauthenticate") {
        this.internalComms.reauthenticate();
        return;
      }
      if (state === "LoggedIn") {
        const { session } = responseBody;
        if (session.softExpiresIn !== 0) {
          session.softExpiry = Date.now() + session.softExpiresIn;
          session.softExpiresIn = 0;
        }
        if (session.expiresIn !== 0) {
          session.hardExpiry = Date.now() + session.expiresIn;
          session.expiresIn = 0;
        }
        const sessionHasTimedOut = (session == null ? void 0 : session.hardExpiry) !== void 0 && session.hardExpiry < Date.now();
        let isOffgrid = sessionHasTimedOut;
        let refreshAt = session.softExpiry;
        if (offgridTimeout > 0) {
          refreshAt = Date.now() + offgridTimeout;
          isOffgrid = true;
        }
        if (session.hardExpiry > Date.now() && session.hardExpiry < refreshAt) {
          refreshAt = session.hardExpiry;
        }
        if (refreshAt <= Date.now()) {
          refreshAt = Date.now() + DEFAULT_OFFGRID_MILLIS;
        }
        const sessionState = {
          isOffgrid,
          expires: refreshAt,
          sessionData: session
        };
        await this._archiveKnownSessionState(sessionState);
        if (!wasOffgrid && sessionHasTimedOut) {
          this.dispatchUpdateEvent("going_offgrid", { detail: this._session });
        }
        if (wasOffgrid && !sessionHasTimedOut) {
          this.dispatchUpdateEvent("going_ongrid", { detail: this._session });
        }
        if (expectedSub && session.user.sub !== expectedSub) {
          return await this._handleUnexpectedIdentityFromServer(sessionState, identityChangeInProgress, throwOnError);
        }
        if (this.internalComms.onSessionDataLoaded) {
          try {
            await this.internalComms.onSessionDataLoaded(session);
          } catch (error) {
            this.logger.error("Got error while handling session data response", error);
            const eventName = loginInProgress ? "login_error" : identityChangeInProgress ? "changeidentity_error" : "refresh_error";
            this.dispatchCustomEvent(eventName, { detail: { error } });
            if (throwOnError) {
              throw error;
            }
            return;
          }
        }
        this.sessionState.expires = refreshAt;
        this.sessionState.isOffgrid = isOffgrid;
        this.sessionState.sessionData = session;
        if (refreshAt) {
          this._scheduleRefresh(refreshAt);
        }
        if (identityChangeInProgress) {
          this.dispatchUpdateEvent("changeidentity_success", { detail: this._session });
        } else if (loginInProgress) {
          this.dispatchUpdateEvent("login_success", { detail: this._session });
        } else {
          this.dispatchUpdateEvent("refresh_success", { detail: this._session });
        }
        this.logger.debug("update state:", sessionState);
        try {
          this._persistCurrentSessionState(sessionState);
        } catch (err) {
          this._handleError(err);
        }
      }
    } else if (offgridTimeout > 0) {
      this.sessionState.expires = Date.now() + offgridTimeout;
      this.sessionState.isOffgrid = true;
      try {
        this._persistCurrentSessionState(this.sessionState);
      } catch (err) {
        this._handleError(err);
      }
    }
  }
  goOffgrid(offgridTimeout) {
    this._updateState(void 0, offgridTimeout != null ? offgridTimeout : DEFAULT_OFFGRID_MILLIS);
  }
  goOngrid() {
    this._updateState(void 0, 0);
  }
  async logout() {
    this._updateState({ state: "LoggedOut", userAction: "Nothing" });
  }
  _handleError(err, throwOnError = false) {
    this.logger.error(err);
    this.dispatchErrorEvent({ name: "error", message: err.message });
    if (throwOnError) {
      throw err;
    }
  }
  _filterOutUnavailableIdentity(candidate) {
    const current = getStoredState(CURRENT_STATE_STORAGE_KEY);
    if (current == null ? void 0 : current.sessionData) {
      const matches = current == null ? void 0 : current.sessionData.identities.filter((identity) => identity.sub == candidate);
      if (matches.length > 0) {
        return candidate;
      }
    }
    return void 0;
  }
  dispatchUpdateEvent(eventName, details) {
    this.internalComms.eventDispatchFunc(new CustomEvent(eventName, details));
    this.internalComms.eventDispatchFunc(new CustomEvent("update", details));
  }
  dispatchCustomEvent(eventName, details) {
    this.internalComms.eventDispatchFunc(new CustomEvent(eventName, details));
  }
  dispatchErrorEvent(error) {
    this.internalComms.eventDispatchFunc(new ErrorEvent("error", error));
  }
  async checkOfGridAndToken() {
    if (this._session.isLoggedIn && this._session.isOffgrid || this._session.isLoggedIn && this._session.isExpired) {
      const message = `Refreshed token. Status Loggedin ${this._session.isLoggedIn} status Is offGrid ${this._session.isOffgrid} status is expired: ${this._session.isExpired}`;
      await delay2(150);
      await this._refresh();
      this.logger.debug(message);
    }
  }
};
_session = new WeakMap();
_fetchSessionPromise = new WeakMap();
_refreshTimeoutId = new WeakMap();
_nextRefresh = new WeakMap();
_lastForceRefresh = new WeakMap();

// src/login-client/login-client.ts
var DEFAULT_CONFIG = {
  logLevel: LogLevel.error,
  logger: console.log,
  enableTabCoordinationViaSessionStorage: true
};
var _LoginClient = class extends EventTarget {
  constructor(config, name = "LoginClient", internalComms) {
    super();
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.logger = new Logger(name, config.logHandler);
    this.core = new ClientCore(this.config, this.logger, internalComms);
    this.debug = new Debug(this, this.core);
  }
  get version() {
    return _LoginClient.version;
  }
  async getSession() {
    return await this.core.getSession();
  }
  async getSessionState() {
    return await this.core.sessionState;
  }
  async forceRefresh() {
    return await this._refresh();
  }
  async migrateRefreshToken(_) {
    this.logger.warn(`deprecated`);
    return await this.getSession();
  }
  async updateProfile(sub, data) {
    const session = await this.core.getSession();
    if (!session.isLoggedIn) {
      throw Error("user is not logged in");
    }
    if (session.isOffgrid) {
      throw Error("authority offgrid");
    }
    if (session.user) {
      const href = `${ENDPOINT_PROFILE_DATA}/${sub}`;
      const response = await put(href, data, session);
      if (getCookie(SERVER_SIGNAL_COOKIE_NAME) === "refresh") {
        await this._refresh();
        clearCookie(SERVER_SIGNAL_COOKIE_NAME);
      } else if (response.loginSignal === "refresh") {
        await this._refresh();
      } else if (session.user.sub === sub) {
        await this._refresh();
      }
    }
    return session;
  }
  async changeIdentity(sub) {
    return await this.core._changeIdentity(sub);
  }
  async postponeUserProblem() {
    var _a;
    const sess = await this.getSession();
    const postponeApiUrl = (_a = sess.userProblem) == null ? void 0 : _a.postponeApiUrl;
    if (!postponeApiUrl) {
      throw Error("Missing postpone api url");
    }
    await post(postponeApiUrl, void 0, sess);
    await this._refresh();
  }
  addEventListener(type, listener) {
    const simpleEventListener = listener;
    super.addEventListener(type, simpleEventListener);
    if (type === "update" && listener !== null) {
      Promise.resolve().then(() => {
        simpleEventListener(new CustomEvent("update", { detail: this.core._session }));
      });
    }
  }
  removeEventListener(type, listener) {
    super.removeEventListener(type, listener);
  }
  _bootstrap() {
    try {
      this.logger.info("bootstrapping login client");
      this.core.bootstrap();
    } catch (err) {
      this._handleError(err);
    }
  }
  _refresh(wantedSub) {
    return this.core._refresh(wantedSub);
  }
  _handleError(err, throwOnError = false) {
    this.logger.error(err);
    this.dispatchEvent(new ErrorEvent("error", { error: err, message: err.message }));
    if (throwOnError) {
      throw err;
    }
  }
  async _handleSessionDataFetchResponse(response, expectedSub, throwOnError) {
    return this.core._handleSessionDataFetchResponse(response, expectedSub, throwOnError);
  }
  async generateInviteCode(codeChallenge) {
    const { body, error, offgridTimeout } = await post(ENDPOINT_GENERATE_INVITE_CODE, {
      codeChallenge: codeChallenge != null ? codeChallenge : ""
    }, await this.getSession());
    if (offgridTimeout) {
      this.core.goOffgrid(offgridTimeout);
    }
    if (error) {
      this._handleError(error);
      throw error;
    } else if (body) {
      return body.inviteCode;
    } else {
      const error2 = new Error("No body returned from generateInviteCode endpoint");
      this._handleError(error2);
      throw error2;
    }
  }
  async _getContextInfo() {
    var _a;
    if (this.contextInfo !== void 0) {
      return this.contextInfo;
    }
    const storedContextInfo = getCookie(CONTEXT_INFO_COOKIE_KEY);
    if (storedContextInfo) {
      this.contextInfo = JSON.parse(decodeURIComponent(storedContextInfo));
    }
    if ((_a = this.contextInfo) == null ? void 0 : _a.authority) {
      return this.contextInfo;
    }
    this.contextInfo = (await get(ENDPOINT_CONTEXT_INFO)).body;
    return this.contextInfo;
  }
  async _updateContextInfo() {
    this.contextInfo = (await get(ENDPOINT_CONTEXT_INFO)).body;
    await this._contextInfoUpdated();
  }
  async _contextInfoUpdated() {
  }
  async getUrlWithExpiryExtended(feature, scope) {
    const { body, error } = await post(ENDPOINT_GENERATE_URL_WITH_EXPIRY + "/" + feature.key, {
      scope,
      ...feature
    }, await this.getSession());
    if (error) {
      throw error;
    } else if (body) {
      return body;
    } else {
      const error2 = new Error("No body returned from generateUrlWithExpiry endpoint");
      this._handleError(error2);
      throw error2;
    }
  }
  async getUrlWithExpiry(kind, scope) {
    const { body, error } = await post(ENDPOINT_GENERATE_URL_WITH_EXPIRY + "/" + kind, {
      scope
    }, await this.getSession());
    if (error) {
      throw error;
    } else if (body) {
      return body;
    } else {
      const error2 = new Error("No body returned from generateUrlWithExpiry endpoint");
      this._handleError(error2);
      throw error2;
    }
  }
  get _nextRefresh() {
    return this.core._nextRefresh;
  }
};
var LoginClient = _LoginClient;
LoginClient.version = "5.5.0";

// src/redirect-login-client/opsession.ts
var COOKIE_KEY_OPSESSIONSTATE = "nrk-login-session-state";
var ENDPOINT_OPSESSIONSTATE = "/auth/opsession/state";
var LOGIN_ATTEMPTED_COOKIE_NAME = "opsession_login_attempted";
var OpSessionConfig = class {
  constructor(opOrigin, opSessionIframeUrl, opSessionInterval, opLoginOnFirstMessageOnly, opLogoutOnFirstMessageOnly, opSessionIframeId, overrideOpOrigin, overrideOpSessionIframeUrl) {
    this.firstMessage = true;
    this.opSessionIframeId = opSessionIframeId;
    this.opSessionInterval = opSessionInterval;
    this.opOrigin = opOrigin;
    this.opSessionIframeUrl = opSessionIframeUrl;
    this.opLoginOnFirstMessageOnly = opLoginOnFirstMessageOnly;
    this.opLogoutOnFirstMessageOnly = opLogoutOnFirstMessageOnly;
    this.overrideOpOrigin = overrideOpOrigin;
    this.overrideOpSessionIframeUrl = overrideOpSessionIframeUrl;
  }
};
var OpSessionEvent = class {
  constructor() {
    this.data = "";
  }
};
var _client2, _timer, _listening, _failCounter, _attemptCounter;
var OpSessionHandler = class {
  constructor(client, contextInfo, config) {
    __privateAdd(this, _client2, void 0);
    __privateAdd(this, _timer, void 0);
    __privateAdd(this, _listening, false);
    __privateAdd(this, _failCounter, 0);
    __privateAdd(this, _attemptCounter, 0);
    this.opSessionState = "";
    this.iframe = void 0;
    __privateSet(this, _client2, client);
    this.config = this._computeConfig("", config);
    this.logger = new Logger("opsession");
    if (contextInfo === void 0) {
      this.logger.error("contextInfo is undefined");
      return;
    }
    this._adjustConfig(contextInfo);
    this.context = contextInfo;
  }
  get _client() {
    return __privateGet(this, _client2);
  }
  get _timer() {
    return __privateGet(this, _timer);
  }
  get _listening() {
    return __privateGet(this, _listening);
  }
  get _failCounter() {
    return __privateGet(this, _failCounter);
  }
  get _attemptCounter() {
    return __privateGet(this, _attemptCounter);
  }
  inIframe() {
    try {
      return window.self !== window.top;
    } catch (e) {
      return true;
    }
  }
  _adjustConfig(contextInfo) {
    this.config.opOrigin = this.config.overrideOpOrigin || contextInfo.authority;
    this.config.opSessionIframeUrl = this.config.overrideOpSessionIframeUrl || `${contextInfo.authority}/nonstandard/opsession`;
    this.config.firstMessage = true;
  }
  async init(contextInfo) {
    var _a, _b;
    this.stop();
    if (this.inIframe()) {
      this.logger.debug("OpSession is not supported from within iframe, will not start");
      return;
    }
    if (contextInfo === void 0) {
      this.logger.warn("OpSessionHandler needs contextInfo to work, will not start");
      return;
    }
    if (getCookie("login_opsession_deactivate") === "1") {
      this.logger.debug("OpSession is deactivated from serverside, (we are most likely in a app/webview context)");
      return;
    }
    const session = await __privateGet(this, _client2).getSession();
    if (session.isOffgrid) {
      this.logger.warn("We are offgrid now, OpSession will not start)");
      return;
    }
    const storedOpSessionState = getCookie(COOKIE_KEY_OPSESSIONSTATE);
    if (storedOpSessionState) {
      this.opSessionState = storedOpSessionState;
    } else {
      const sessionResponse = await get(ENDPOINT_OPSESSIONSTATE);
      this.opSessionState = (_b = (_a = sessionResponse.body) == null ? void 0 : _a.opSessionState) != null ? _b : "none";
    }
    if (this.opSessionState === "reauthenticate") {
      __privateGet(this, _client2)._performOpSessionLogin();
      return;
    }
    if (this.opSessionState === "offgrid") {
      this.logger.info("Offgrid mode: no check will be made against authority opsession frame");
      return;
    }
    this.iframe = document.createElement("iframe");
    this.iframe.id = this.config.opSessionIframeId;
    this.iframe.src = `${this.config.opSessionIframeUrl}?d=${new Date().getTime()}`;
    this.iframe.style.display = "none";
    __privateSet(this, _timer, window.setInterval(() => this._periodicCheck(), this.config.opSessionInterval));
    if (!__privateGet(this, _listening)) {
      this._eventListener = (e) => {
        {
          if (e.origin === this.config.opOrigin) {
            {
              if (e.data !== "unchanged") {
                {
                  window.clearInterval(__privateGet(this, _timer));
                  __privateSet(this, _listening, false);
                }
              }
              this.handle(e);
            }
          }
        }
      };
      window.addEventListener("message", this._eventListener);
      __privateSet(this, _listening, true);
    }
    document.body.appendChild(this.iframe);
  }
  stop(wipeAllTraces = false) {
    __privateSet(this, _failCounter, 0);
    __privateSet(this, _attemptCounter, 0);
    if (__privateGet(this, _timer)) {
      window.clearInterval(__privateGet(this, _timer));
    }
    if (this.iframe) {
      this.iframe.remove();
    }
    if (this._eventListener) {
      window.removeEventListener("message", this._eventListener);
      this._eventListener = void 0;
    }
    if (wipeAllTraces) {
      clearCookie(LOGIN_ATTEMPTED_COOKIE_NAME);
    }
  }
  _periodicCheck() {
    var _a, _b;
    try {
      __privateWrapper(this, _attemptCounter)._++;
      const op = (_a = this.iframe) == null ? void 0 : _a.contentWindow;
      op == null ? void 0 : op.postMessage(`${(_b = this.context) == null ? void 0 : _b.clientId} ${this.opSessionState}`, "*");
    } catch (ex) {
      __privateWrapper(this, _failCounter)._++;
      if (__privateGet(this, _failCounter) > 5) {
        this.logger.warn("Error connecting to the opsession frame, giving up");
        this.stop();
      }
    }
  }
  _computeConfig(authority, config) {
    var _a;
    return new OpSessionConfig((config == null ? void 0 : config.overrideOpOrigin) || authority, (_a = config == null ? void 0 : config.overrideOpSessionIframeUrl) != null ? _a : `${authority}/nonstandard/opsession`, (config == null ? void 0 : config.opSessionInterval) || 2e3, (config == null ? void 0 : config.opLoginOnFirstMessageOnly) || true, (config == null ? void 0 : config.opLogoutOnFirstMessageOnly) || false, (config == null ? void 0 : config.opSessionIframeId) || "opsession_frame", config == null ? void 0 : config.overrideOpOrigin, config == null ? void 0 : config.overrideOpSessionIframeUrl);
  }
  handle(e) {
    const shouldHandleLogin = this.config.firstMessage || !this.config.opLoginOnFirstMessageOnly;
    const shouldHandleLogout = this.config.firstMessage || !this.config.opLogoutOnFirstMessageOnly;
    switch (e.data) {
      case "changed": {
        const counter = parseInt(getCookie(LOGIN_ATTEMPTED_COOKIE_NAME) || "0");
        if (shouldHandleLogin && counter < 3) {
          setCookie(LOGIN_ATTEMPTED_COOKIE_NAME, "" + (counter + 1), 3 * 60 * 1e3);
          __privateGet(this, _client2)._performOpSessionLogin();
        }
        break;
      }
      case "ended":
        clearCookie(LOGIN_ATTEMPTED_COOKIE_NAME);
        if (shouldHandleLogout) {
          __privateGet(this, _client2)._performOpSessionLogout();
        }
        break;
      case "unchanged":
        clearCookie(LOGIN_ATTEMPTED_COOKIE_NAME);
        break;
    }
    this.config.firstMessage = false;
  }
};
_client2 = new WeakMap();
_timer = new WeakMap();
_listening = new WeakMap();
_failCounter = new WeakMap();
_attemptCounter = new WeakMap();

// src/redirect-login-client/constants.ts
var REAUTHENTICATED_COOKIE_KEY = "nrk-login-reauthenticated";

// src/redirect-login-client/redirect-login-client.ts
var RedirectLoginClient = class extends LoginClient {
  constructor(config, onSessionDataLoaded) {
    const internalComms = {
      reauthenticate: () => {
      },
      logout: () => {
      },
      onSessionDataLoaded: onSessionDataLoaded != null ? onSessionDataLoaded : async () => {
        await new Promise((resolve) => resolve());
      },
      eventDispatchFunc: (_) => true
    };
    super(config, "RedirectLoginClient", internalComms);
    internalComms.reauthenticate = this._reauthenticate.bind(this);
    internalComms.logout = this._localLogout.bind(this);
    internalComms.eventDispatchFunc = this.dispatchEvent.bind(this);
    this._bootstrap();
  }
  async initOpSession() {
    if (this.isLocalDev() && !this.isATestRun()) {
      console.warn("opsession disabled for http and localhost");
      return;
    }
    const contextInfo = await this._getContextInfo();
    this.opSession = new OpSessionHandler(this, contextInfo, this.config);
    if ((contextInfo == null ? void 0 : contextInfo.authority) !== void 0) {
      await this.opSession.init(contextInfo);
    }
  }
  isLocalDev() {
    return window.location.protocol === "http:" || window.location.hostname === "localhost";
  }
  isATestRun() {
    return window.location.protocol === "http:" && window.location.hostname === "localhost" && window.location.port === "8888";
  }
  login(options = {}) {
    var _a;
    if (this.core._session.isOffgrid) {
      throw Error(`authority offgrid: unable to make attempt for ${this.core._session.expires - Date.now()}ms`);
    }
    const url = new URL("/auth/web/login", window.location.href);
    for (const key in options) {
      const value = options[key];
      if (value) {
        url.searchParams.set(key, Array.isArray(value) ? value.join(",") : value.toString());
      }
    }
    let returnUrl = new URL((_a = options.returnUrl) != null ? _a : window.location.href, window.location.href);
    if (options.selectIdentityOnAuthority) {
      returnUrl = this._getSelectIdentityOnAuthorityUrl(returnUrl);
    } else if (options.profileType === "Child") {
      returnUrl = this._getPickChildProfileUrl(returnUrl);
    }
    this._navigateWithReturnUrl(url, returnUrl.href);
  }
  _performOpSessionLogin() {
    this._navigateWithReturnUrl(new URL("/auth/web/login/opsession", window.location.href));
  }
  logout(options = {}) {
    const { returnUrl = window.location.href, suppressUserConfirmation = true } = options;
    const url = new URL(suppressUserConfirmation ? "/auth/web/logout/central" : "/auth/web/logout/tentative", window.location.href);
    this._navigateWithReturnUrl(url, returnUrl);
  }
  _performOpSessionLogout() {
    this._navigateWithReturnUrl(new URL("/auth/web/logout/opsession", window.location.href));
  }
  _navigate(url, disableNavigationSinceThisIsATest = false) {
    if (!disableNavigationSinceThisIsATest) {
      window.location.href = url.href;
    }
    return url;
  }
  _navigateWithReturnUrl(url, returnUrl, disableNavigationSinceThisIsATest = false) {
    const returnUrlUrlString = returnUrl != null ? returnUrl : window.location.href;
    url.searchParams.set("returnUrl", returnUrlUrlString);
    return this._navigate(url, disableNavigationSinceThisIsATest);
  }
  async navigateTo(target) {
    var _a;
    const urlHolder = await this._resolveNavigationTarget(target);
    if (urlHolder.body) {
      this._navigate(new URL((_a = urlHolder.body) == null ? void 0 : _a.urlComplete, window.location.href));
    }
  }
  async _resolveNavigationTarget(target) {
    target.returnUrl = target.returnUrl || new URL(window.location.href);
    return post("/auth/url/generate", target, await this.getSession());
  }
  async fixUserProblem(userproblem) {
    const resolved = await this._resolveNavigationTarget({
      target: "urlcomplete",
      urlComplete: userproblem.fixUrlComplete
    });
    if (resolved.body) {
      this._navigate(new URL(resolved.body.urlComplete, window.location.href));
    }
  }
  _localLogout() {
    this._navigateWithReturnUrl(new URL("/auth/web/logout/local", window.location.href));
  }
  _getSelectIdentityOnAuthorityUrl(returnUrl) {
    var _a;
    const url = new URL("/auth/web/selectidentity", window.location.href);
    url.searchParams.set("returnUrl", (_a = returnUrl == null ? void 0 : returnUrl.href) != null ? _a : window.location.href);
    return url;
  }
  _getPickChildProfileUrl(returnUrl) {
    var _a;
    const url = new URL("/auth/web/selectchildprofileidentity", window.location.href);
    url.searchParams.set("returnUrl", (_a = returnUrl == null ? void 0 : returnUrl.href) != null ? _a : window.location.href);
    return url;
  }
  async selectIdentityOnAuthority() {
    const url = this._getSelectIdentityOnAuthorityUrl();
    this._navigate(url);
  }
  _reauthenticate() {
    if (getCookie(REAUTHENTICATED_COOKIE_KEY) !== "1") {
      setCookie(REAUTHENTICATED_COOKIE_KEY, "1", 1e3 * 60 * 5);
      this._navigateWithReturnUrl(new URL("/auth/web/login/reauthenticate", window.location.href));
    } else {
      this.logger.warn("skipping reauthentication since we already reauthenticated less than 5 minutes ago");
    }
  }
  async _contextInfoUpdated() {
    var _a;
    (_a = this.opSession) == null ? void 0 : _a.init(this.contextInfo);
  }
  async ensureUserConformsToRequirements(input) {
    const session = await this.getSession();
    if (!session.isLoggedIn) {
      const loginOptions = Object.assign({}, input);
      loginOptions.returnUrl = window.location.href;
      this.login(loginOptions);
      return;
    }
    if (!session.userConformsToRequirements(input)) {
      const url = await this._resolveNavigationTarget({
        target: "fixuserrequirements",
        requirements: (input.requirements || []).join(","),
        requirementsIntro: input.requirementsIntro || "",
        outro: encodeURIComponent(input.outro || "")
      });
      if (url.body) {
        this._navigateWithReturnUrl(new URL(url.body.urlComplete, window.location.href));
      }
    }
  }
};

// src/redirect-login-client.ts
var authClient;
function getRedirectLoginClient(config, onSessionDataLoaded) {
  if (config.logLevel !== void 0) {
    setGlobalLogLevel(config.logLevel);
  }
  if (!authClient) {
    authClient = new RedirectLoginClient(config, onSessionDataLoaded);
  }
  return authClient;
}
export {
  BackoffRetryError,
  ClientCore,
  Debug,
  HTTPError,
  LogLevel,
  Logger,
  LoginClient,
  LoginSession,
  OpSessionConfig,
  OpSessionEvent,
  OpSessionHandler,
  RedirectLoginClient,
  getRedirectLoginClient,
  setGlobalLogLevel
};
